import React, { useEffect, useState } from "react";
import {
  FaArrowRight,
  FaBullhorn,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaGraduationCap,
  FaHardHat,
  FaPlay,
  FaTools,
  FaWrench,
  FaCertificate,
  FaBriefcase,
  FaBolt,
  FaUserTie,
  FaLeaf,
  FaSeedling,
  FaRecycle,
} from "react-icons/fa";
import api from "../api/api";

const HomeHeader = () => {
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await api.get("/api/slides/");

        const data =
          response.data?.results ||
          response.data;

        setSlides(
          Array.isArray(data)
            ? data
            : []
        );
      } catch (err) {
        console.error(
          "Failed to load slides:",
          err
        );

        setError(
          "Unable to load school information."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, []);

  useEffect(() => {
    if (
      slides.length <= 1 ||
      isPaused
    ) {
      return;
    }

    const timer = setInterval(() => {
      setCurrentIndex(
        (previous) =>
          (previous + 1) %
          slides.length
      );
    }, 7500);

    return () => clearInterval(timer);
  }, [
    slides.length,
    isPaused,
  ]);

  const nextSlide = () => {
    if (!slides.length) return;

    setCurrentIndex(
      (previous) =>
        (previous + 1) %
        slides.length
    );
  };

  const previousSlide = () => {
    if (!slides.length) return;

    setCurrentIndex(
      (previous) =>
        (previous - 1 + slides.length) %
        slides.length
    );
  };

  const selectSlide = (index) => {
    setCurrentIndex(index);
  };

  const pageStyles = `
    .passion-header {
      --passion-purple: #6b21a8;
      --passion-purple-dark: #4c1478;
      --passion-purple-deep: #35104f;
      --passion-purple-light: #9333ea;

      --passion-green: #15803d;
      --passion-green-dark: #11632f;
      --passion-green-light: #22c55e;

      --passion-lemon: #d9f99d;
      --passion-lemon-bright: #bef264;
      --passion-lime: #84cc16;

      --passion-white: #ffffff;
      --passion-off-white: #f8fafc;
      --passion-surface: #f4f7f5;
      --passion-border: #e5e7eb;

      --passion-text: #172033;
      --passion-muted: #64748b;

      position: relative;
      width: 100%;
      min-height: 720px;
      height: 88vh;
      overflow: hidden;

      background:
        linear-gradient(
          135deg,
          #ffffff 0%,
          #fbfdff 48%,
          #f2f9f3 100%
        );

      color: var(--passion-text);

      font-family:
        "Poppins",
        "Inter",
        Arial,
        sans-serif;

      isolation: isolate;
    }

    /* ==========================================================
       MAIN BACKGROUND
    ========================================================== */

    .passion-background {
      position: absolute;
      inset: 0;
      overflow: hidden;

      background:
        radial-gradient(
          circle at 85% 20%,
          rgba(217, 249, 157, 0.42),
          transparent 27%
        ),
        radial-gradient(
          circle at 75% 75%,
          rgba(107, 33, 168, 0.08),
          transparent 35%
        ),
        linear-gradient(
          135deg,
          #ffffff 0%,
          #fbfdfb 55%,
          #f0f8f1 100%
        );
    }

    .passion-slide {
      position: absolute;
      inset: 0;

      opacity: 0;
      visibility: hidden;

      transition:
        opacity 1s ease,
        visibility 1s ease;
    }

    .passion-slide.active {
      opacity: 1;
      visibility: visible;
    }

    .passion-image {
      position: absolute;
      inset: 0;

      background-size: cover;
      background-position: center;

      transform: scale(1.06);

      transition:
        transform 10s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        );
    }

    .passion-slide.active
      .passion-image {
      transform: scale(1);
    }

    .passion-image::after {
      content: "";

      position: absolute;
      inset: 0;

      background:
        linear-gradient(
          90deg,
          rgba(255,255,255,0.97) 0%,
          rgba(255,255,255,0.92) 28%,
          rgba(255,255,255,0.67) 51%,
          rgba(255,255,255,0.10) 100%
        ),
        linear-gradient(
          0deg,
          rgba(255,255,255,0.78),
          transparent 48%
        );
    }

    /* ==========================================================
       BRAND DECORATION
    ========================================================== */

    .passion-background::before {
      content: "";

      position: absolute;
      z-index: 2;

      width: 570px;
      height: 570px;

      right: -190px;
      top: -210px;

      border-radius: 50%;

      border:
        1px solid
        rgba(107,33,168,0.14);

      box-shadow:
        0 0 0 35px
        rgba(107,33,168,0.035),

        0 0 0 75px
        rgba(132,204,22,0.035),

        0 0 0 120px
        rgba(21,128,61,0.025);

      animation:
        passionPulse
        10s
        ease-in-out
        infinite;
    }

    .passion-background::after {
      content: "";

      position: absolute;
      z-index: 3;

      width: 850px;
      height: 300px;

      right: -270px;
      bottom: -95px;

      border-radius: 50%;

      border-top:
        2px solid
        rgba(107,33,168,0.15);

      transform:
        rotate(-17deg);

      animation:
        passionWave
        9s
        ease-in-out
        infinite;
    }

    @keyframes passionPulse {
      0%,
      100% {
        transform:
          scale(0.95)
          rotate(0deg);

        opacity: 0.55;
      }

      50% {
        transform:
          scale(1.08)
          rotate(12deg);

        opacity: 1;
      }
    }

    @keyframes passionWave {
      0%,
      100% {
        transform:
          translateX(35px)
          rotate(-17deg)
          scaleX(0.94);

        opacity: 0.35;
      }

      50% {
        transform:
          translateX(-65px)
          rotate(-12deg)
          scaleX(1.08);

        opacity: 0.75;
      }
    }

    /* ==========================================================
       BRAND SHAPE
    ========================================================== */

    .passion-brand-shape {
      position: absolute;
      z-index: 7;

      top: -140px;
      right: 18%;

      width: 250px;
      height: 900px;

      background:
        linear-gradient(
          180deg,
          rgba(107,33,168,0.92),
          rgba(21,128,61,0.88)
        );

      transform:
        rotate(16deg);

      opacity: 0.10;

      border-radius:
        0 0 35px 35px;

      animation:
        passionShape
        10s
        ease-in-out
        infinite;
    }

    @keyframes passionShape {
      0%,
      100% {
        transform:
          rotate(16deg)
          translateY(0);
      }

      50% {
        transform:
          rotate(20deg)
          translateY(-20px);
      }
    }

    /* ==========================================================
       LEMON RING
    ========================================================== */

    .passion-lemon-ring {
      position: absolute;
      z-index: 8;

      right: 5%;
      bottom: -175px;

      width: 350px;
      height: 350px;

      border-radius: 50%;

      border:
        48px solid
        rgba(190,242,100,0.32);

      box-shadow:
        0 0 70px
        rgba(132,204,22,0.12);

      animation:
        lemonRing
        11s
        ease-in-out
        infinite;
    }

    @keyframes lemonRing {
      0%,
      100% {
        transform:
          translateY(0)
          scale(0.96)
          rotate(0deg);
      }

      50% {
        transform:
          translateY(-30px)
          scale(1.08)
          rotate(15deg);
      }
    }

    /* ==========================================================
       GRID
    ========================================================== */

    .passion-grid {
      position: absolute;
      z-index: 6;

      inset: 0;

      opacity: 0.055;

      background-image:
        linear-gradient(
          rgba(107,33,168,0.7) 1px,
          transparent 1px
        ),
        linear-gradient(
          90deg,
          rgba(107,33,168,0.7) 1px,
          transparent 1px
        );

      background-size:
        55px 55px;

      transform:
        perspective(600px)
        rotateX(58deg)
        scale(1.4);

      transform-origin:
        bottom;

      animation:
        passionGrid
        18s
        linear
        infinite;

      pointer-events: none;
    }

    @keyframes passionGrid {
      from {
        background-position:
          0 0,
          0 0;
      }

      to {
        background-position:
          0 55px,
          55px 0;
      }
    }

    /* ==========================================================
       FLOATING PARTICLES
    ========================================================== */

    .passion-particles {
      position: absolute;
      z-index: 9;

      inset: 0;

      overflow: hidden;

      pointer-events: none;
    }

    .passion-particle {
      position: absolute;

      width: 5px;
      height: 5px;

      border-radius: 50%;

      background:
        var(--passion-purple);

      box-shadow:
        0 0 12px
        rgba(107,33,168,0.30);

      animation:
        passionParticle
        var(--duration)
        ease-in-out
        infinite;

      animation-delay:
        var(--delay);
    }

    .passion-particle:nth-child(1) {
      left: 18%;
      bottom: 8%;

      --duration: 7s;
      --delay: -2s;
    }

    .passion-particle:nth-child(2) {
      left: 35%;
      bottom: 20%;

      --duration: 9s;
      --delay: -5s;
    }

    .passion-particle:nth-child(3) {
      left: 53%;
      bottom: 5%;

      --duration: 8s;
      --delay: -1s;

      background:
        var(--passion-green);
    }

    .passion-particle:nth-child(4) {
      left: 72%;
      bottom: 15%;

      --duration: 10s;
      --delay: -6s;

      background:
        var(--passion-lime);
    }

    .passion-particle:nth-child(5) {
      left: 83%;
      bottom: 25%;

      --duration: 7.5s;
      --delay: -3s;
    }

    .passion-particle:nth-child(6) {
      left: 61%;
      bottom: 32%;

      --duration: 11s;
      --delay: -8s;

      background:
        var(--passion-lemon-bright);
    }

    @keyframes passionParticle {
      0% {
        transform:
          translate3d(
            0,
            50px,
            0
          )
          scale(0.5);

        opacity: 0;
      }

      15% {
        opacity: 0.8;
      }

      50% {
        transform:
          translate3d(
            35px,
            -170px,
            0
          )
          scale(1);

        opacity: 0.65;
      }

      100% {
        transform:
          translate3d(
            -20px,
            -350px,
            0
          )
          scale(0.25);

        opacity: 0;
      }
    }

    /* ==========================================================
       TOP NAVIGATION
    ========================================================== */

    .passion-top {
      position: absolute;
      z-index: 40;

      top: 0;
      left: 0;
      right: 0;

      min-height: 88px;

      display: flex;
      align-items: center;
      justify-content: space-between;

      padding:
        0 5%;

      background:
        rgba(255,255,255,0.96);

      border-bottom:
        1px solid
        rgba(107,33,168,0.12);

      box-shadow:
        0 8px 35px
        rgba(20,30,25,0.07);

      backdrop-filter:
        blur(16px);
    }

    .passion-logo {
      display: flex;
      align-items: center;

      gap: 13px;

      min-width: 0;
    }

    .passion-logo-icon {
      width: 48px;
      height: 48px;

      flex-shrink: 0;

      display: flex;
      align-items: center;
      justify-content: center;

      color: white;

      background:
        linear-gradient(
          135deg,
          var(--passion-purple),
          var(--passion-green)
        );

      border-radius:
        13px;

      font-size: 21px;

      box-shadow:
        0 9px 22px
        rgba(107,33,168,0.20);

      transform:
        rotate(-3deg);

      animation:
        logoFloat
        4s
        ease-in-out
        infinite;
    }

    @keyframes logoFloat {
      0%,
      100% {
        transform:
          rotate(-3deg)
          translateY(0);
      }

      50% {
        transform:
          rotate(2deg)
          translateY(-3px);
      }
    }

    .passion-logo-text strong {
      display: block;

      color:
        var(--passion-purple-dark);

      font-size: 15px;

      line-height: 1.1;

      font-weight: 950;

      letter-spacing:
        -0.02em;
    }

    .passion-logo-text span {
      display: block;

      margin-top: 5px;

      color:
        var(--passion-green);

      font-size: 8px;

      line-height: 1;

      font-weight: 800;

      letter-spacing:
        0.16em;

      text-transform:
        uppercase;
    }

    .passion-top-right {
      display: flex;
      align-items: center;

      gap: 9px;
    }

    .passion-top-link {
      display: inline-flex;
      align-items: center;

      gap: 7px;

      padding:
        10px 14px;

      color:
        var(--passion-text);

      background:
        white;

      border:
        1px solid
        var(--passion-border);

      border-radius:
        8px;

      font-size: 10px;

      font-weight: 800;

      text-transform:
        uppercase;

      text-decoration:
        none;

      transition:
        0.3s ease;
    }

    .passion-top-link svg {
      color:
        var(--passion-purple);
    }

    .passion-top-link:hover {
      color:
        white;

      background:
        var(--passion-purple);

      border-color:
        var(--passion-purple);

      transform:
        translateY(-2px);

      box-shadow:
        0 9px 22px
        rgba(107,33,168,0.18);
    }

    .passion-top-link:hover svg {
      color:
        var(--passion-lemon-bright);
    }

    /* ==========================================================
       MAIN CONTENT
    ========================================================== */

    .passion-main {
      position: relative;
      z-index: 20;

      height: 100%;

      display: flex;
      align-items: center;

      padding:
        125px
        5%
        90px;
    }

    .passion-copy {
      width:
        min(
          680px,
          58%
        );

      color:
        var(--passion-text);

      animation:
        passionEntry
        1s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        )
        both;
    }

    @keyframes passionEntry {
      from {
        opacity: 0;

        transform:
          translateX(-40px)
          translateY(12px);

        filter:
          blur(4px);
      }

      to {
        opacity: 1;

        transform:
          translateX(0)
          translateY(0);

        filter:
          blur(0);
      }
    }

    /* ==========================================================
       BRAND LABEL
    ========================================================== */

    .passion-tag {
      display: inline-flex;
      align-items: center;

      gap: 8px;

      margin-bottom: 22px;

      padding:
        9px 14px;

      color:
        var(--passion-purple-dark);

      background:
        var(--passion-lemon);

      border:
        1px solid
        rgba(132,204,22,0.30);

      border-radius:
        7px;

      font-size: 9px;

      font-weight: 950;

      letter-spacing:
        0.12em;

      text-transform:
        uppercase;

      box-shadow:
        0 7px 18px
        rgba(132,204,22,0.12);
    }

    .passion-tag svg {
      color:
        var(--passion-green);
    }

    /* ==========================================================
       HERO TITLE
    ========================================================== */

    .passion-title {
      margin: 0;

      color:
        var(--passion-text);

      font-size:
        clamp(
          44px,
          5.8vw,
          78px
        );

      line-height:
        0.98;

      font-weight:
        950;

      letter-spacing:
        -0.065em;
    }

    .passion-title-accent {
      display: block;

      color:
        var(--passion-purple);
    }

    .passion-title-green {
      color:
        var(--passion-green);
    }

    .passion-line {
      width: 125px;
      height: 6px;

      margin:
        24px 0 22px;

      background:
        linear-gradient(
          90deg,
          var(--passion-purple),
          var(--passion-green),
          var(--passion-lemon-bright)
        );

      border-radius:
        10px;
    }

    /* ==========================================================
       DESCRIPTION
    ========================================================== */

    .passion-description {
      max-width: 650px;

      margin: 0;

      color:
        #526173;

      font-size:
        clamp(
          14px,
          1.25vw,
          17px
        );

      line-height:
        1.78;
    }

    /* ==========================================================
       ACTIONS
    ========================================================== */

    .passion-actions {
      display: flex;
      flex-wrap: wrap;

      gap: 12px;

      margin-top: 30px;
    }

    .passion-primary,
    .passion-secondary {
      min-height: 52px;

      display: inline-flex;
      align-items: center;
      justify-content: center;

      gap: 10px;

      padding:
        0 21px;

      border-radius:
        8px;

      font-size: 11px;

      font-weight: 900;

      text-transform:
        uppercase;

      text-decoration:
        none;

      transition:
        0.3s ease;
    }

    .passion-primary {
      color: white;

      background:
        linear-gradient(
          135deg,
          var(--passion-purple),
          var(--passion-green)
        );

      box-shadow:
        7px 7px 0
        var(--passion-lemon-bright);
    }

    .passion-primary:hover {
      transform:
        translate(
          3px,
          3px
        );

      box-shadow:
        3px 3px 0
        var(--passion-lemon-bright);

      background:
        linear-gradient(
          135deg,
          var(--passion-purple-light),
          var(--passion-green)
        );
    }

    .passion-secondary {
      color:
        var(--passion-purple-dark);

      background:
        rgba(255,255,255,0.88);

      border:
        1px solid
        rgba(107,33,168,0.18);

      box-shadow:
        0 7px 20px
        rgba(20,30,25,0.05);
    }

    .passion-secondary:hover {
      color:
        white;

      background:
        var(--passion-green);

      border-color:
        var(--passion-green);

      transform:
        translateY(-3px);
    }

    .passion-play {
      width: 29px;
      height: 29px;

      display: flex;
      align-items: center;
      justify-content: center;

      color: white;

      background:
        var(--passion-purple);

      border-radius:
        50%;

      font-size: 8px;
    }

    /* ==========================================================
       BENEFITS
    ========================================================== */

    .passion-benefits {
      display: flex;
      flex-wrap: wrap;

      gap: 17px;

      margin-top: 28px;
    }

    .passion-benefit {
      display: flex;
      align-items: center;

      gap: 7px;

      color:
        #556375;

      font-size: 10px;

      font-weight: 750;
    }

    .passion-benefit svg {
      color:
        var(--passion-green);
    }

    /* ==========================================================
       RIGHT INFORMATION PANEL
    ========================================================== */

    .passion-panel {
      position: absolute;
      z-index: 25;

      right: 5%;
      top: 50%;

      width: 365px;

      transform:
        translateY(-42%);

      animation:
        passionPanel
        1.1s
        0.15s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        )
        both;
    }

    @keyframes passionPanel {
      from {
        opacity: 0;

        transform:
          translateY(-38%)
          translateX(50px);
      }

      to {
        opacity: 1;

        transform:
          translateY(-42%)
          translateX(0);
      }
    }

    .passion-panel-main {
      overflow: hidden;

      background:
        rgba(255,255,255,0.96);

      border:
        1px solid
        rgba(107,33,168,0.12);

      border-radius:
        16px;

      box-shadow:
        0 25px 70px
        rgba(43,35,55,0.15);

      backdrop-filter:
        blur(15px);
    }

    .passion-panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      padding:
        16px 20px;

      color: white;

      background:
        linear-gradient(
          135deg,
          var(--passion-purple),
          var(--passion-green)
        );
    }

    .passion-panel-header strong {
      font-size: 10px;

      font-weight: 900;

      letter-spacing:
        0.08em;

      text-transform:
        uppercase;
    }

    .passion-panel-header span {
      padding:
        5px 8px;

      color:
        var(--passion-purple-dark);

      background:
        var(--passion-lemon);

      border-radius:
        5px;

      font-size: 7px;

      font-weight: 950;

      text-transform:
        uppercase;
    }

    .passion-panel-body {
      padding:
        24px 22px;
    }

    .passion-panel-icon {
      width: 56px;
      height: 56px;

      display: flex;
      align-items: center;
      justify-content: center;

      margin-bottom: 17px;

      color: white;

      background:
        linear-gradient(
          135deg,
          var(--passion-green),
          var(--passion-purple)
        );

      border-radius:
        12px;

      font-size: 23px;

      box-shadow:
        0 10px 25px
        rgba(21,128,61,0.17);

      animation:
        panelIcon
        4.5s
        ease-in-out
        infinite;
    }

    @keyframes panelIcon {
      0%,
      100% {
        transform:
          translateY(0)
          rotate(0);
      }

      50% {
        transform:
          translateY(-5px)
          rotate(3deg);
      }
    }

    .passion-panel-body h3 {
      margin: 0;

      color:
        var(--passion-text);

      font-size: 25px;

      line-height:
        1.12;

      font-weight:
        950;

      letter-spacing:
        -0.035em;
    }

    .passion-panel-body p {
      margin:
        11px 0 20px;

      color:
        var(--passion-muted);

      font-size: 10px;

      line-height:
        1.7;
    }

    /* ==========================================================
       PROGRAM CARDS
    ========================================================== */

    .passion-programs {
      display: grid;

      grid-template-columns:
        1fr 1fr;

      gap: 8px;
    }

    .passion-program {
      min-height: 52px;

      display: flex;
      align-items: center;

      gap: 8px;

      padding:
        8px 10px;

      color:
        #475569;

      background:
        #f8fafc;

      border:
        1px solid
        #e6eaf0;

      border-radius:
        8px;

      font-size: 8px;

      font-weight: 800;

      transition:
        0.3s ease;
    }

    .passion-program:hover {
      color:
        var(--passion-purple-dark);

      border-color:
        rgba(107,33,168,0.25);

      background:
        var(--passion-lemon);

      transform:
        translateY(-2px);
    }

    .passion-program svg {
      flex-shrink: 0;

      color:
        var(--passion-green);

      font-size: 13px;
    }

    /* ==========================================================
       PANEL FOOTER
    ========================================================== */

    .passion-panel-footer {
      display: grid;

      grid-template-columns:
        repeat(3, 1fr);

      border-top:
        1px solid
        #e8ebef;
    }

    .passion-stat {
      padding:
        16px 8px;

      text-align:
        center;

      border-right:
        1px solid
        #e8ebef;
    }

    .passion-stat:last-child {
      border-right: 0;
    }

    .passion-stat strong {
      display: block;

      color:
        var(--passion-purple);

      font-size: 19px;

      font-weight: 950;
    }

    .passion-stat span {
      display: block;

      margin-top: 3px;

      color:
        #8490a0;

      font-size: 7px;

      font-weight: 850;

      text-transform:
        uppercase;
    }

    /* ==========================================================
       ANNOUNCEMENT
    ========================================================== */

    .passion-announcement {
      position: absolute;
      z-index: 30;

      right:
        calc(
          5% + 285px
        );

      bottom: 85px;

      width: 250px;

      padding:
        15px;

      background:
        rgba(255,255,255,0.96);

      border-left:
        4px solid
        var(--passion-green);

      border-radius:
        0 10px 10px 0;

      box-shadow:
        0 15px 35px
        rgba(30,40,35,0.12);

      backdrop-filter:
        blur(10px);
    }

    .passion-announcement-top {
      display: flex;
      align-items: center;

      gap: 9px;

      margin-bottom: 9px;
    }

    .passion-announcement-icon {
      width: 31px;
      height: 31px;

      display: flex;
      align-items: center;
      justify-content: center;

      color: white;

      background:
        var(--passion-purple);

      border-radius:
        7px;

      font-size: 11px;
    }

    .passion-announcement small {
      color:
        var(--passion-green);

      font-size: 7px;

      font-weight: 950;

      letter-spacing:
        0.12em;

      text-transform:
        uppercase;
    }

    .passion-announcement h4 {
      margin: 2px 0 0;

      color:
        var(--passion-text);

      font-size: 10px;

      font-weight: 900;
    }

    .passion-announcement p {
      margin: 0;

      color:
        var(--passion-muted);

      font-size: 8px;

      line-height:
        1.6;
    }

    /* ==========================================================
       SLIDE COUNTER
    ========================================================== */

    .passion-counter {
      position: absolute;
      z-index: 35;

      left: 5%;
      bottom: 29px;

      display: flex;
      align-items: center;

      gap: 10px;

      color:
        #7b8797;

      font-size: 9px;

      font-weight: 850;
    }

    .passion-counter strong {
      color:
        var(--passion-purple);

      font-size: 20px;
    }

    .passion-counter-line {
      width: 45px;
      height: 2px;

      background:
        linear-gradient(
          90deg,
          var(--passion-purple),
          var(--passion-green)
        );
    }

    /* ==========================================================
       INDICATORS
    ========================================================== */

    .passion-indicators {
      position: absolute;
      z-index: 35;

      left: 50%;
      bottom: 34px;

      display: flex;

      gap: 7px;

      transform:
        translateX(-50%);
    }

    .passion-indicator {
      width: 27px;
      height: 4px;

      padding: 0;

      border: 0;

      border-radius:
        10px;

      background:
        #d7dde5;

      cursor: pointer;

      transition:
        0.4s ease;
    }

    .passion-indicator.active {
      width: 52px;

      background:
        linear-gradient(
          90deg,
          var(--passion-purple),
          var(--passion-green)
        );

      box-shadow:
        0 0 10px
        rgba(107,33,168,0.20);
    }

    /* ==========================================================
       ARROWS
    ========================================================== */

    .passion-controls {
      position: absolute;
      z-index: 35;

      right: 5%;
      bottom: 20px;

      display: flex;

      gap: 7px;
    }

    .passion-arrow {
      width: 43px;
      height: 43px;

      display: flex;
      align-items: center;
      justify-content: center;

      color:
        var(--passion-purple-dark);

      background:
        rgba(255,255,255,0.96);

      border:
        1px solid
        rgba(107,33,168,0.15);

      border-radius:
        8px;

      cursor: pointer;

      transition:
        0.3s ease;

      box-shadow:
        0 7px 20px
        rgba(30,40,35,0.08);
    }

    .passion-arrow:hover {
      color: white;

      background:
        var(--passion-purple);

      border-color:
        var(--passion-purple);

      transform:
        translateY(-3px);

      box-shadow:
        0 10px 25px
        rgba(107,33,168,0.20);
    }

    /* ==========================================================
       LOADING / ERROR
    ========================================================== */

    .passion-loader {
      min-height: 650px;

      width: 100%;

      display: flex;
      align-items: center;
      justify-content: center;

      background:
        linear-gradient(
          135deg,
          #ffffff,
          #f1f8f2
        );

      color:
        var(--passion-text);

      font-family:
        "Poppins",
        Arial,
        sans-serif;
    }

    .passion-loader-inner {
      text-align: center;
    }

    .passion-loader-icon {
      width: 70px;
      height: 70px;

      display: flex;
      align-items: center;
      justify-content: center;

      margin:
        0 auto 20px;

      color: white;

      background:
        linear-gradient(
          135deg,
          var(--passion-purple),
          var(--passion-green)
        );

      border-radius:
        16px;

      font-size: 27px;

      box-shadow:
        0 12px 30px
        rgba(107,33,168,0.18);

      animation:
        loaderFloat
        3s
        ease-in-out
        infinite;
    }

    @keyframes loaderFloat {
      0%,
      100% {
        transform:
          translateY(0)
          rotate(0);
      }

      50% {
        transform:
          translateY(-7px)
          rotate(4deg);
      }
    }

    .passion-spinner {
      width: 28px;
      height: 28px;

      margin:
        0 auto 18px;

      border:
        3px solid
        #e8eaf0;

      border-top-color:
        var(--passion-purple);

      border-right-color:
        var(--passion-green);

      border-radius: 50%;

      animation:
        passionSpin
        0.8s
        linear
        infinite;
    }

    @keyframes passionSpin {
      to {
        transform:
          rotate(360deg);
      }
    }

    .passion-loader h3 {
      margin: 0;

      color:
        var(--passion-text);

      font-size: 18px;

      font-weight: 850;
    }

    .passion-loader p {
      margin:
        7px 0 0;

      color:
        var(--passion-muted);

      font-size: 11px;
    }

    .passion-error-icon {
      width: 65px;
      height: 65px;

      display: flex;
      align-items: center;
      justify-content: center;

      margin:
        0 auto 20px;

      color: white;

      background:
        #b42318;

      border-radius:
        14px;

      font-size: 25px;

      font-weight: 900;
    }

    .passion-retry {
      margin-top: 20px;

      padding:
        11px 22px;

      border: 0;

      border-radius:
        7px;

      color: white;

      background:
        var(--passion-purple);

      font-weight: 850;

      cursor: pointer;

      transition:
        0.3s ease;
    }

    .passion-retry:hover {
      background:
        var(--passion-green);

      transform:
        translateY(-2px);
    }

    /* ==========================================================
       TABLET
    ========================================================== */

    @media (max-width: 1100px) {
      .passion-panel {
        width: 310px;
        right: 3%;
      }

      .passion-copy {
        width: 64%;
      }

      .passion-announcement {
        right:
          calc(
            3% + 235px
          );
      }

      .passion-brand-shape {
        right: 12%;
      }
    }

    /* ==========================================================
       SMALL TABLET
    ========================================================== */

    @media (max-width: 850px) {
      .passion-header {
        min-height: 820px;
      }

      .passion-main {
        min-height: 820px;

        align-items:
          flex-start;

        padding:
          130px
          5%
          120px;
      }

      .passion-copy {
        width: 72%;
      }

      .passion-panel {
        width: 285px;

        right: 3%;

        opacity: 0.93;
      }

      .passion-announcement {
        display: none;
      }

      .passion-brand-shape {
        right: 5%;
      }

      .passion-top-link {
        display: none;
      }
    }

    /* ==========================================================
       MOBILE
    ========================================================== */

    @media (max-width: 650px) {
      .passion-header {
        min-height: 760px;
        height: auto;
      }

      .passion-top {
        min-height: 72px;

        padding:
          0 20px;
      }

      .passion-logo-icon {
        width: 39px;
        height: 39px;

        border-radius:
          10px;

        font-size: 17px;
      }

      .passion-logo-text strong {
        font-size: 11px;
      }

      .passion-logo-text span {
        font-size: 7px;
      }

      .passion-main {
        min-height: 760px;

        padding:
          115px
          20px
          105px;
      }

      .passion-copy {
        width: 100%;
      }

      .passion-title {
        font-size:
          clamp(
            39px,
            12vw,
            58px
          );
      }

      .passion-description {
        font-size: 13px;

        line-height:
          1.72;
      }

      .passion-panel {
        display: none;
      }

      .passion-brand-shape {
        right: -110px;

        opacity: 0.07;
      }

      .passion-lemon-ring {
        right: -160px;

        opacity: 0.28;
      }

      .passion-actions {
        flex-direction: column;
        align-items: stretch;
      }

      .passion-primary,
      .passion-secondary {
        width: 100%;
      }

      .passion-benefits {
        display: grid;

        grid-template-columns:
          1fr 1fr;

        gap: 13px;
      }

      .passion-counter {
        left: 20px;
        bottom: 25px;
      }

      .passion-controls {
        right: 20px;
        bottom: 18px;
      }

      .passion-indicators {
        bottom: 75px;
      }

      .passion-arrow {
        width: 39px;
        height: 39px;
      }

      .passion-particle {
        opacity: 0.5;
      }
    }

    /* ==========================================================
       SMALL PHONE
    ========================================================== */

    @media (max-width: 400px) {
      .passion-title {
        font-size: 37px;
      }

      .passion-tag {
        font-size: 8px;
      }

      .passion-benefits {
        grid-template-columns:
          1fr;
      }

      .passion-indicators {
        display: none;
      }
    }

    /* ==========================================================
       REDUCED MOTION
    ========================================================== */

    @media (prefers-reduced-motion: reduce) {
      .passion-slide,
      .passion-image,
      .passion-copy,
      .passion-panel,
      .passion-primary,
      .passion-secondary,
      .passion-arrow,
      .passion-brand-shape,
      .passion-lemon-ring,
      .passion-particle,
      .passion-grid,
      .passion-background::before,
      .passion-background::after,
      .passion-logo-icon,
      .passion-panel-icon {
        transition: none !important;
        animation: none !important;
      }
    }
  `;

  /* ==========================================================
     LOADING
  ========================================================== */

  if (loading) {
    return (
      <>
        <style>{pageStyles}</style>

        <section className="passion-loader">
          <div className="passion-loader-inner">

            <div className="passion-loader-icon">
              <FaLeaf />
            </div>

            <div className="passion-spinner"></div>

            <h3>
              Preparing Your Learning Experience
            </h3>

            <p>
              Loading PASSION AIDS THE FUTURE...
            </p>

          </div>
        </section>
      </>
    );
  }

  /* ==========================================================
     ERROR
  ========================================================== */

  if (error) {
    return (
      <>
        <style>{pageStyles}</style>

        <section className="passion-loader">
          <div className="passion-loader-inner">

            <div className="passion-error-icon">
              !
            </div>

            <h3>
              Unable to Load
            </h3>

            <p>
              {error}
            </p>

            <button
              className="passion-retry"
              onClick={() =>
                window.location.reload()
              }
            >
              TRY AGAIN
            </button>

          </div>
        </section>
      </>
    );
  }

  /* ==========================================================
     EMPTY SLIDES
  ========================================================== */

  if (!slides.length) {
    return (
      <>
        <style>{pageStyles}</style>

        <section className="passion-loader">
          <div className="passion-loader-inner">

            <div className="passion-loader-icon">
              <FaSeedling />
            </div>

            <h3>
              PASSION AIDS THE FUTURE
            </h3>

            <p>
              Accessible education.
              Practical skills.
              Professional opportunity.
            </p>

          </div>
        </section>
      </>
    );
  }

  const currentSlide =
    slides[currentIndex];

  return (
    <>
      <style>{pageStyles}</style>

      <header
        className="passion-header"
        onMouseEnter={() =>
          setIsPaused(true)
        }
        onMouseLeave={() =>
          setIsPaused(false)
        }
      >

        {/* ====================================================
            BACKGROUND
        ==================================================== */}

        <div className="passion-background">

          {slides.map(
            (slide, index) => (
              <div
                key={
                  slide.id ||
                  index
                }
                className={`passion-slide ${
                  currentIndex === index
                    ? "active"
                    : ""
                }`}
              >
                <div
                  className="passion-image"
                  style={{
                    backgroundImage:
                      `url("${slide.image}")`,
                  }}
                />
              </div>
            )
          )}

          <div className="passion-brand-shape"></div>

          <div className="passion-lemon-ring"></div>

          <div className="passion-grid"></div>

          <div className="passion-particles">

            <span className="passion-particle"></span>
            <span className="passion-particle"></span>
            <span className="passion-particle"></span>
            <span className="passion-particle"></span>
            <span className="passion-particle"></span>
            <span className="passion-particle"></span>

          </div>

        </div>

        {/* ====================================================
            TOP NAVIGATION
        ==================================================== */}

        <div className="passion-top">

          <div className="passion-logo">

            <div className="passion-logo-icon">
              <FaSeedling />
            </div>

            <div className="passion-logo-text">

              <strong>
                PASSION AIDS THE FUTURE
              </strong>

              <span>
                Education • Skills • Opportunity
              </span>

            </div>

          </div>

          <div className="passion-top-right">

            <a
              href="#programs"
              className="passion-top-link"
            >
              <FaTools />
              Programs
            </a>

            <a
              href="#admissions"
              className="passion-top-link"
            >
              <FaGraduationCap />
              Admissions
            </a>

          </div>

        </div>

        {/* ====================================================
            MAIN HERO
        ==================================================== */}

        <main className="passion-main">

          <div
            className="passion-copy"
            key={currentIndex}
          >

            <div className="passion-tag">
              <FaSeedling />
              EDUCATION FOR THE FUTURE
            </div>

            <h1 className="passion-title">

              {currentSlide.title}

            </h1>

            <div className="passion-line"></div>

            <p className="passion-description">
              {currentSlide.description}
            </p>

            <div className="passion-actions">

              <a
                href="#programs"
                className="passion-primary"
              >
                Explore Programs
                <FaArrowRight />
              </a>

              <a
                href="#about"
                className="passion-secondary"
              >
                <span className="passion-play">
                  <FaPlay />
                </span>

                About PASSION AIDS
              </a>

            </div>

            <div className="passion-benefits">

              <div className="passion-benefit">
                <FaCheckCircle />
                Practical Education
              </div>

              <div className="passion-benefit">
                <FaCheckCircle />
                Professional Skills
              </div>

              <div className="passion-benefit">
                <FaCheckCircle />
                Leadership Development
              </div>

              <div className="passion-benefit">
                <FaCheckCircle />
                Career Preparation
              </div>

            </div>

          </div>

        </main>

        {/* ====================================================
            RIGHT INFORMATION PANEL
        ==================================================== */}

        <aside className="passion-panel">

          <div className="passion-panel-main">

            <div className="passion-panel-header">

              <strong>
                PASSION AIDS THE FUTURE
              </strong>

              <span>
                Enrolling Now
              </span>

            </div>

            <div className="passion-panel-body">

              <div className="passion-panel-icon">
                <FaGraduationCap />
              </div>

              <h3>
                Learn.
                <br />
                Grow. Lead.
              </h3>

              <p>
                We provide accessible, practical
                and professional education that
                develops knowledge, skills,
                leadership capacity and workplace
                competence.
              </p>

              <div className="passion-programs">

                <div className="passion-program">
                  <FaGraduationCap />
                  Professional Education
                </div>

                <div className="passion-program">
                  <FaTools />
                  Practical Skills
                </div>

                <div className="passion-program">
                  <FaUserTie />
                  Leadership
                </div>

                <div className="passion-program">
                  <FaBriefcase />
                  Career Development
                </div>

                <div className="passion-program">
                  <FaCertificate />
                  Certification
                </div>

                <div className="passion-program">
                  <FaSeedling />
                  Community Impact
                </div>

              </div>

            </div>

            <div className="passion-panel-footer">

              <div className="passion-stat">
                <strong>01</strong>
                <span>Learn</span>
              </div>

              <div className="passion-stat">
                <strong>02</strong>
                <span>Develop</span>
              </div>

              <div className="passion-stat">
                <strong>03</strong>
                <span>Lead</span>
              </div>

            </div>

          </div>

        </aside>

        {/* ====================================================
            ANNOUNCEMENT
        ==================================================== */}

        <div className="passion-announcement">

          <div className="passion-announcement-top">

            <div className="passion-announcement-icon">
              <FaBullhorn />
            </div>

            <div>

              <small>
                Opportunity
              </small>

              <h4>
                Build Your Future
              </h4>

            </div>

          </div>

          <p>
            Discover accessible education,
            practical training and professional
            development opportunities designed
            to help you succeed.
          </p>

        </div>

        {/* ====================================================
            SLIDE COUNTER
        ==================================================== */}

        {slides.length > 1 && (
          <div className="passion-counter">

            <strong>
              {String(
                currentIndex + 1
              ).padStart(2, "0")}
            </strong>

            <div className="passion-counter-line"></div>

            <span>
              {String(
                slides.length
              ).padStart(2, "0")}
            </span>

          </div>
        )}

        {/* ====================================================
            INDICATORS
        ==================================================== */}

        {slides.length > 1 && (
          <div className="passion-indicators">

            {slides.map(
              (_, index) => (
                <button
                  key={index}
                  className={`passion-indicator ${
                    currentIndex === index
                      ? "active"
                      : ""
                  }`}
                  onClick={() =>
                    selectSlide(index)
                  }
                  aria-label={
                    `Go to slide ${
                      index + 1
                    }`
                  }
                />
              )
            )}

          </div>
        )}

        {/* ====================================================
            CONTROLS
        ==================================================== */}

        {slides.length > 1 && (
          <div className="passion-controls">

            <button
              className="passion-arrow"
              onClick={
                previousSlide
              }
              aria-label="Previous slide"
            >
              <FaChevronLeft />
            </button>

            <button
              className="passion-arrow"
              onClick={
                nextSlide
              }
              aria-label="Next slide"
            >
              <FaChevronRight />
            </button>

          </div>
        )}

      </header>
    </>
  );
};

export default HomeHeader;


